import type { CRNodeData } from '@/lib/graph-layout';
import { cn } from '@/lib/utils';
import { Handle, NodeProps, Position } from '@xyflow/react';
import {
  Boxes,
  Bug,
  Building2,
  ChevronDown,
  ChevronRight,
  CircleUser,
  ClipboardCheck,
  Crosshair,
  Database,
  Eye,
  FileText,
  Flag,
  FolderTree,
  GitCompareArrows,
  type LucideIcon,
  MapPin,
  MessageSquareQuote,
  Microscope,
  Network,
  Radar,
  Server,
  ShieldAlert,
  Siren,
  StickyNote,
  Swords,
  User,
  UserRoundX,
  Users,
  Wrench
} from 'lucide-react';
import { memo } from 'react';
import { useRecoilValue } from 'recoil';

import { Badge } from '@/components/ui/badge';
import { activeTargetIdState } from '@/state/graphSelection';

// Lucide glyphs rather than a bespoke STIX icon set: every published STIX icon
// pack is CC BY-SA 4.0, whose share-alike clause attaches the moment an icon is
// recolored. These inherit `currentColor`, so the per-type color the backend
// already sends in `CRNodeData.color` finally reaches the glyph itself.
const STIX_ICONS: Record<string, LucideIcon> = {
  'attack-pattern': Crosshair,
  campaign: Flag,
  'course-of-action': ClipboardCheck,
  grouping: Boxes,
  'identity-class': Users,
  'identity-generic': CircleUser,
  'identity-group': Users,
  'identity-individual': User,
  'identity-organization': Building2,
  'identity-system': Server,
  indicator: Radar,
  infrastructure: Network,
  'intrusion-set': Swords,
  location: MapPin,
  malware: Bug,
  'malware-analysis': Microscope,
  'malware-family': FolderTree,
  note: StickyNote,
  'observed-data': Database,
  opinion: MessageSquareQuote,
  relationship: GitCompareArrows,
  report: FileText,
  sighting: Eye,
  'threat-actor': UserRoundX,
  tool: Wrench,
  vulnerability: ShieldAlert,
  incident: Siren
};

const STATUS_STYLES: Record<string, string> = {
  'IN PROGRESS':
    'bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/40',
  COMPLETE:
    'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/40',
  TODO: 'bg-sky-500/20 text-sky-700 dark:text-sky-400 border-sky-500/40',
  FAILED: 'bg-rose-700/25 text-rose-700 dark:text-rose-300 border-rose-700/50',
  SKIPPED:
    'bg-orange-400/25 text-orange-600 dark:text-orange-200 border-orange-400/50'
};

function riskTone(risk: number | undefined): string {
  if (risk === undefined) return 'bg-muted text-muted-foreground border-border';
  if (risk > 10)
    return 'bg-red-500/25 text-red-700 dark:text-red-300 border-red-500/50';
  if (risk > 5)
    return 'bg-orange-500/25 text-orange-700 dark:text-orange-300 border-orange-500/50';
  return 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/40';
}

const CRNode = memo(function CRNode({ data }: NodeProps) {
  const {
    label,
    color,
    icon,
    risk,
    status,
    hasSubnodes,
    subnodeCount,
    isExpanded,
    isSelected,
    direction,
    raw
  } = data as CRNodeData;

  const activeTargetId = useRecoilValue(activeTargetIdState);
  const isRoot = raw?.is_root === true || raw?.status === 'captured';
  const isActiveRoot =
    isRoot && !!activeTargetId && raw?.target_id === activeTargetId;

  const StixIcon = icon ? STIX_ICONS[icon] : undefined;
  const targetPosition = direction === 'LR' ? Position.Left : Position.Top;
  const sourcePosition = direction === 'LR' ? Position.Right : Position.Bottom;

  return (
    <div
      className={cn(
        'group relative flex items-center gap-3 rounded-xl border bg-card/95 px-4 py-3 shadow-md backdrop-blur',
        'transition-all duration-150',
        'hover:shadow-lg hover:-translate-y-[1px]',
        isSelected
          ? 'border-primary ring-2 ring-primary/40'
          : isActiveRoot
            ? 'border-emerald-500/60 ring-2 ring-emerald-500/50'
            : 'border-border/80'
      )}
      style={{
        borderLeft: `5px solid ${color}`,
        width: 250,
        minHeight: 84
      }}
    >
      <Handle
        type="target"
        position={targetPosition}
        className="!h-2 !w-2 !border-0 !bg-muted-foreground/50"
      />
      <div
        className="flex h-10 w-10 flex-none items-center justify-center rounded-lg"
        style={{
          backgroundColor: `${color}22`,
          border: `1px solid ${color}55`
        }}
      >
        {StixIcon ? (
          <StixIcon className="h-6 w-6" style={{ color }} aria-hidden="true" />
        ) : (
          <span className="text-xs font-bold uppercase" style={{ color }}>
            {label.slice(0, 2)}
          </span>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div
          className="text-[13px] font-semibold leading-tight text-foreground line-clamp-2 [overflow-wrap:anywhere]"
          title={label}
        >
          {label}
        </div>
        {(risk !== undefined || status || isActiveRoot) && (
          <div className="flex flex-wrap items-center gap-1">
            {isActiveRoot && (
              <Badge
                variant="outline"
                className="h-5 border-emerald-500/40 bg-emerald-500/20 px-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400"
              >
                Active
              </Badge>
            )}
            {risk !== undefined && (
              <Badge
                variant="outline"
                className={cn('h-5 px-2 text-[10px]', riskTone(risk))}
              >
                risk {risk}
              </Badge>
            )}
            {status && (
              <Badge
                variant="outline"
                className={cn(
                  'h-5 px-2 text-[10px]',
                  STATUS_STYLES[status] ??
                    'bg-muted text-muted-foreground border-border'
                )}
              >
                {status}
              </Badge>
            )}
          </div>
        )}
      </div>
      {hasSubnodes && (
        <div
          className="flex flex-none items-center gap-0.5 text-muted-foreground"
          title={
            isExpanded
              ? `Right-click to collapse ${subnodeCount} child${subnodeCount === 1 ? '' : 'ren'} (Shift + right-click for all descendants)`
              : `Right-click to expand ${subnodeCount} child${subnodeCount === 1 ? '' : 'ren'}`
          }
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          ) : (
            <>
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
              <span className="rounded-full border border-border bg-muted/60 px-1 text-[10px] font-semibold leading-4 text-muted-foreground">
                +{subnodeCount}
              </span>
            </>
          )}
        </div>
      )}
      <Handle
        type="source"
        position={sourcePosition}
        className="!h-2 !w-2 !border-0 !bg-muted-foreground/50"
      />
    </div>
  );
});

export default CRNode;
